// src/components/Safety/SafetyFeedbackModal.tsx
// Shown at publish time when layers 1 or 2 fail.
// Lists specific issues so the writer knows exactly what to fix.
import React from 'react';
import { SafetyIssue } from '../../hooks/useSafetyCheck';
 
const C = {
  primary:          '#6a37d4',
  primaryLight:     '#ae8dff',
  primaryContainer: '#ede8ff',
  secondary:        '#006947',
  secondaryContainer: '#d1fae5',
  tertiary:         '#6f5900',
  tertiaryContainer:'#fed01b',
  error:            '#b41340',
  errorContainer:   '#ffd9df',
  onBackground:     '#252f3c',
  onSurface:        '#252f3c',
  onSurfaceVariant: '#525c6a',
  surfaceHigh:      '#d6e3f9',
};
 
const CATEGORY_META: Record<string, { icon: string; label: string; bg: string; border: string; color: string }> = {
  profanity:     { icon: '🚫', label: 'Language',      bg: C.errorContainer,       border: `${C.error}50`,           color: C.error },
  pii:           { icon: '🔒', label: 'Private Info',  bg: `${C.tertiaryContainer}80`, border: `${C.tertiary}40`,    color: C.tertiary },
  bullying:      { icon: '💬', label: 'Kindness',      bg: C.errorContainer,       border: `${C.error}50`,           color: C.error },
  inappropriate: { icon: '⚠️', label: 'Content',       bg: `${C.tertiaryContainer}80`, border: `${C.tertiary}40`,    color: C.tertiary },
  contact:       { icon: '🔒', label: 'Contact Info',  bg: `${C.tertiaryContainer}80`, border: `${C.tertiary}40`,    color: C.tertiary },
};
 
interface Props {
  issues: SafetyIssue[];
  onClose: () => void;
}
 
const SafetyFeedbackModal: React.FC<Props> = ({ issues, onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, background: 'rgba(37,47,60,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 2000, padding: '24px', backdropFilter: 'blur(6px)',
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: '#fff', borderRadius: '24px', padding: '36px',
        maxWidth: '480px', width: '100%',
        boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        animation: 'safetyModalIn .2s ease-out',
      }}
    >
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <div style={{ fontSize: '3rem', marginBottom: '12px' }}>✍️</div>
        <h2 style={{
          fontFamily: "'Epilogue', sans-serif", fontWeight: '900',
          fontSize: '1.4rem', color: C.onBackground, margin: '0 0 8px 0',
        }}>
          Almost there!
        </h2>
        <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: 0, lineHeight: '1.6' }}>
          Fix these things in your story before it can be published:
        </p>
      </div>
 
      {/* Issue list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '28px' }}>
        {issues.map((issue, i) => {
          const meta = CATEGORY_META[issue.category] ?? CATEGORY_META.profanity;
          return (
            <div
              key={i}
              style={{
                display: 'flex', gap: '14px', alignItems: 'flex-start',
                background: meta.bg, border: `1.5px solid ${meta.border}`,
                borderRadius: '14px', padding: '16px',
              }}
            >
              <div style={{ fontSize: '1.4rem', flexShrink: 0, marginTop: '2px' }}>{meta.icon}</div>
              <div>
                <div style={{
                  fontFamily: "'Epilogue', sans-serif", fontWeight: '800',
                  fontSize: '0.85rem', color: meta.color, marginBottom: '3px',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                }}>
                  {meta.label}
                </div>
                <div style={{ fontSize: '0.9rem', color: C.onSurface, lineHeight: '1.5' }}>
                  {issue.detail}
                </div>
              </div>
            </div>
          );
        })}
      </div>
 
      {/* Golden rule reminder */}
      <div style={{
        background: C.primaryContainer, borderRadius: '12px',
        padding: '14px 18px', marginBottom: '24px',
        border: `1.5px solid ${C.primaryLight}40`,
      }}>
        <div style={{
          fontSize: '0.82rem', color: C.primary, fontWeight: '700', lineHeight: '1.6',
        }}>
          💡 Fix the highlighted parts in your story, then try publishing again. Your story is saved — nothing will be lost!
        </div>
      </div>
 
      {/* Action */}
      <button
        onClick={onClose}
        style={{
          width: '100%', padding: '14px',
          background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
          color: '#fff', border: 'none', borderRadius: '9999px',
          fontFamily: "'Epilogue', sans-serif", fontSize: '0.95rem', fontWeight: '800',
          cursor: 'pointer', boxShadow: `0 4px 14px ${C.primary}40`,
        }}
      >
        Go Fix It ✏️
      </button>
    </div>
 
    <style>{`
      @keyframes safetyModalIn {
        from { opacity: 0; transform: scale(0.95) translateY(8px); }
        to   { opacity: 1; transform: scale(1)    translateY(0); }
      }
    `}</style>
  </div>
);
 
export default SafetyFeedbackModal;